import { useEffect, useMemo, useState } from 'react';
import { useAppStore, type ServiceHealth } from '@callhub/shared';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  DIALER_WIDGET_DRAG_LOCK_EVENT,
  DIALER_WIDGET_MODE_EVENT,
  DIALER_WIDGET_MODE_OPTIONS,
  emitDialerWidgetDragLockChange,
  emitDialerWidgetModeChange,
  normalizeDialerWidgetMode,
  persistDialerWidgetDragLockToStorage,
  persistDialerWidgetModeToStorage,
  readDialerWidgetDragLockFromStorage,
  readDialerWidgetModeFromStorage,
  type DialerWidgetMode,
} from '@/lib/dialer-widget-mode';
import type { ResolvedTheme, ThemeMode } from '@/lib/theme';

type NavItem = { label: string; icon: string; to: string };

const NAV_SECTIONS: Array<{ title: string; items: NavItem[] }> = [
  {
    title: 'Workspace',
    items: [
      { label: 'Dashboard', icon: 'dashboard', to: '/' },
      { label: 'Comms', icon: 'forum', to: '/dialer' },
      { label: 'Voicemails', icon: 'voicemail', to: '/voicemails' },
      { label: 'Contacts', icon: 'contacts', to: '/contacts' },
      { label: 'Tasks', icon: 'check_circle', to: '/tasks' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Agents', icon: 'group', to: '/agents' },
      { label: 'Analytics', icon: 'bar_chart', to: '/analytics' },
      { label: 'Status', icon: 'monitor_heart', to: '/status' },
      { label: 'Billing', icon: 'receipt_long', to: '/billing' },
    ],
  },
  {
    title: 'Configuration',
    items: [{ label: 'Settings', icon: 'settings', to: '/settings' }],
  },
];

const THEME_OPTIONS: Array<{ mode: ThemeMode; label: string; icon: string }> = [
  { mode: 'auto', label: 'Auto', icon: 'brightness_auto' },
  { mode: 'light', label: 'Light', icon: 'light_mode' },
  { mode: 'dark', label: 'Dark', icon: 'dark_mode' },
];

function healthDotClass(health: ServiceHealth) {
  if (health === 'operational') return 'bg-emerald-400';
  if (health === 'degraded') return 'bg-amber-400';
  if (health === 'outage') return 'bg-red-400';
  if (health === 'maintenance') return 'bg-sky-400';
  return 'bg-muted-foreground';
}

function healthBadgeClass(health: ServiceHealth) {
  if (health === 'operational') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (health === 'degraded') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  if (health === 'outage') return 'bg-red-500/15 text-red-700 dark:text-red-300';
  if (health === 'maintenance') return 'bg-sky-500/15 text-sky-700 dark:text-sky-300';
  return 'bg-muted text-muted-foreground';
}

function healthLabel(health: ServiceHealth) {
  if (health === 'operational') return 'Operational';
  if (health === 'degraded') return 'Degraded';
  if (health === 'outage') return 'Outage';
  if (health === 'maintenance') return 'Maintenance';
  return 'Unknown';
}

type SidebarProps = {
  isMobileOpen: boolean;
  onCloseMobile: () => void;
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  onThemeModeChange: (mode: ThemeMode) => void;
};

export function Sidebar({
  isMobileOpen,
  onCloseMobile,
  themeMode,
  resolvedTheme,
  onThemeModeChange,
}: SidebarProps) {
  const location = useLocation();
  const isConnected = useAppStore((s) => s.isConnected);
  const [isFooterExpanded, setIsFooterExpanded] = useState(false);
  const [dialerWidgetMode, setDialerWidgetMode] = useState<DialerWidgetMode>('two_columns');
  const [isWidgetDragLocked, setIsWidgetDragLocked] = useState(false);
  const serviceStatusQuery = useQuery({
    queryKey: ['service-status'],
    queryFn: async () => {
      const response = await api.getServiceStatus();
      return response.data;
    },
    staleTime: 45_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const chainCount = serviceStatusQuery.data?.chain.length ?? 0;
  const overallHealth: ServiceHealth = serviceStatusQuery.data?.overallHealth ?? 'unknown';
  const statusSummary = useMemo(() => {
    if (serviceStatusQuery.isPending) return 'Loading';
    if (serviceStatusQuery.isError) return 'Status unavailable';
    return healthLabel(overallHealth);
  }, [overallHealth, serviceStatusQuery.isError, serviceStatusQuery.isPending]);
  const isDialerRoute = location.pathname.startsWith('/dialer');

  useEffect(() => {
    if (!isDialerRoute) return;
    setDialerWidgetMode(readDialerWidgetModeFromStorage());
    setIsWidgetDragLocked(readDialerWidgetDragLockFromStorage());
  }, [isDialerRoute, location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ mode?: unknown }>).detail;
      setDialerWidgetMode(normalizeDialerWidgetMode(detail?.mode));
    };
    const handleDragLockChange = (event: Event) => {
      const detail = (event as CustomEvent<{ locked?: unknown }>).detail;
      setIsWidgetDragLocked(detail?.locked === true);
    };

    window.addEventListener(DIALER_WIDGET_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener(DIALER_WIDGET_DRAG_LOCK_EVENT, handleDragLockChange as EventListener);
    return () => {
      window.removeEventListener(DIALER_WIDGET_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener(
        DIALER_WIDGET_DRAG_LOCK_EVENT,
        handleDragLockChange as EventListener,
      );
    };
  }, []);

  const handleDialerWidgetModeChange = (mode: DialerWidgetMode) => {
    setDialerWidgetMode(mode);
    persistDialerWidgetModeToStorage(mode);
    emitDialerWidgetModeChange(mode);
  };
  const handleDialerWidgetDragLockToggle = () => {
    const nextValue = !isWidgetDragLocked;
    setIsWidgetDragLocked(nextValue);
    persistDialerWidgetDragLockToStorage(nextValue);
    emitDialerWidgetDragLockChange(nextValue);
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-50 flex h-screen w-72 flex-col border-r border-border/80 bg-card/95 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)] backdrop-blur transition-transform duration-200 lg:z-40 lg:translate-x-0 ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="border-b border-border/70 px-4 py-4 lg:px-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 text-sm font-bold text-white">
              C
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold tracking-tight text-foreground">
                CallHub
              </h1>
              <p className="truncate text-xs text-muted-foreground">Realtime Support Console</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCloseMobile}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground lg:hidden"
            aria-label="Close sidebar"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>

        <div className="mt-4 rounded-xl border border-border/70 bg-background/70 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Live Workspace
          </p>
          <p className="mt-1 text-sm font-semibold text-foreground">Customer Communications</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Triage messages, calls, and follow-ups.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        <nav className="space-y-5">
          {NAV_SECTIONS.map((section) => (
            <section key={section.title} className="space-y-1.5">
              <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/90">
                {section.title}
              </p>
              {section.items.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    `group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-200 ${
                      isActive
                        ? 'bg-brand-500/16 text-brand-800 dark:text-brand-100'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                  onClick={onCloseMobile}
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`absolute left-1 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full transition-opacity duration-200 ${
                          isActive ? 'bg-brand-500 opacity-100' : 'opacity-0'
                        }`}
                        aria-hidden
                      />
                      <span className="material-symbols-outlined text-[19px]">{item.icon}</span>
                      <span className="truncate font-medium">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </section>
          ))}
        </nav>

        {isDialerRoute ? (
          <div className="mt-5 rounded-xl border border-border/70 bg-background/70 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Dialer Layout
            </p>
            <div className="mt-2 flex items-center gap-1">
              <div className="grid flex-1 grid-cols-2 gap-1 rounded-lg border border-border bg-background p-1">
                {DIALER_WIDGET_MODE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleDialerWidgetModeChange(option.value)}
                    className={`inline-flex items-center justify-center rounded-md px-2 py-1.5 text-xs font-medium transition-colors duration-200 ${
                      dialerWidgetMode === option.value
                        ? 'bg-brand-500/15 text-brand-700 dark:text-brand-200'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    aria-label={`Switch to ${option.label} widget layout`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleDialerWidgetDragLockToggle}
                className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200 ${
                  isWidgetDragLocked
                    ? 'border-brand-500/45 bg-brand-500/15 text-brand-700 dark:text-brand-200'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                aria-label={isWidgetDragLocked ? 'Unlock widget dragging' : 'Lock widget dragging'}
                title={isWidgetDragLocked ? 'Unlock widget dragging' : 'Lock widget dragging'}
              >
                <span className="material-symbols-outlined text-[16px]">
                  {isWidgetDragLocked ? 'lock' : 'lock_open'}
                </span>
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-5 rounded-xl border border-border/70 bg-background/70 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Theme
          </p>
          <div className="mt-2 grid grid-cols-3 gap-1 rounded-lg border border-border bg-background p-1">
            {THEME_OPTIONS.map((option) => (
              <button
                key={option.mode}
                type="button"
                onClick={() => onThemeModeChange(option.mode)}
                className={`inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors duration-200 ${
                  themeMode === option.mode
                    ? 'bg-brand-500/15 text-brand-700 dark:text-brand-200'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                aria-label={`Set ${option.label.toLowerCase()} theme`}
              >
                <span className="material-symbols-outlined text-[14px]">{option.icon}</span>
                <span>{option.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {themeMode === 'auto' ? `Auto (system: ${resolvedTheme})` : `Manual (${resolvedTheme})`}
          </p>
        </div>
      </div>

      <div className="border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={() => setIsFooterExpanded((value) => !value)}
          className="flex w-full items-center justify-between gap-2 text-left"
          aria-expanded={isFooterExpanded}
          aria-label="Toggle service status details"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <span
                className={`h-2 w-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-muted-foreground'}`}
              />
              <span className="text-foreground">{isConnected ? 'Connected' : 'Offline'}</span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 text-xs ${healthBadgeClass(overallHealth)}`}>
                {statusSummary}
              </span>
              <span className="truncate text-xs text-muted-foreground">Service chain</span>
            </div>
          </div>
          <span
            className={`material-symbols-outlined text-[18px] text-muted-foreground transition-transform ${
              isFooterExpanded ? 'rotate-180' : ''
            }`}
          >
            expand_more
          </span>
        </button>

        {isFooterExpanded ? (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { label: 'Cloudflare', provider: serviceStatusQuery.data?.providers.cloudflare },
                  { label: 'Twilio', provider: serviceStatusQuery.data?.providers.twilio },
                ] as const
              ).map((item) => {
                const providerStatus = item.provider?.status ?? 'unknown';
                const globalStatus = item.provider?.globalStatus;
                const showGlobalStatus = !!globalStatus && globalStatus !== providerStatus;

                return (
                  <div
                    key={item.label}
                    className="rounded-lg border border-border bg-background/85 p-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${healthDotClass(providerStatus)}`}
                      />
                      <span className="text-xs font-semibold text-foreground">{item.label}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {healthLabel(providerStatus)}
                    </p>
                    {showGlobalStatus ? (
                      <p className="mt-0.5 text-xs text-muted-foreground/80">
                        Global: {healthLabel(globalStatus)}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {serviceStatusQuery.isError ? (
              <p className="text-xs text-red-400">Service status API unavailable.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {chainCount > 0 ? `${chainCount} services monitored.` : 'No chain data yet.'}
                </p>
                <NavLink
                  to="/status"
                  onClick={onCloseMobile}
                  className="inline-flex w-full items-center justify-between rounded-md border border-border bg-background/85 px-2.5 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground"
                >
                  <span>View detailed status</span>
                  <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                </NavLink>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
